/**
 * ActionGroups Component
 * @module views/modules/ActionGroups
 */

import React, { useMemo, useRef } from 'react';

import PropTypes from 'prop-types';

import ActionGroupDefault from '@ulta/core/components/ActionGroupDefault/ActionGroupDefault';
import ActionGroupMulti from '@ulta/core/components/ActionGroupMulti/ActionGroupMulti';
import { useIntersectionProcessor } from '@ulta/core/hooks/useIntersectionProcessor/useIntersectionProcessor';
import { useAuthContext } from '@ulta/core/providers/AuthProvider/AuthProvider';
import { AUTH0_SCREEN } from '@ulta/core/utils/auth/auth';
import { CLIENT_ACTION_TYPES } from '@ulta/core/utils/constants/action';
import { isServer } from '@ulta/core/utils/device_detection/device_detection';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import { isAuthButton } from '@ulta/utils/actionUtils/actionUtils';
/**
 * Represents a ActionGroups component
 *
 * @method
 * @param { ActionGroupsProps } props
 * @returns ActionGroups
 */

export const ActionGroups = function( props ){
  const actionGroupsRef = useRef( null );
  const {
    // ActionGroup related props
    variant,
    // Array of actions
    actions:originalActions,
    // Styles set in Amplience
    style,
    types,
    // Generated styles based coming from parent components
    alignment,
    fullyClickableParent,
    isKnockoutLink
  } = props;

  // Checks the variant to determine which ActionGroup to display
  const isActionGroupDefault = variant === 'default';
  const isActionGroupMulti = variant === 'multi';

  useIntersectionProcessor( {
    ...props,
    componentRef: actionGroupsRef
  } );

  const { getAuthUrl } = useAuthContext();


  const actions = useMemo( () => {
    return processAuthActionUrls( { originalActions }, { getAuthUrl, isServer, isAuthButton } );
  }, [originalActions, getAuthUrl] );
  return (
    <div
      className='ActionGroups'
      ref={ actionGroupsRef }
    >
      { isActionGroupDefault &&
        <ActionGroupDefault
          action1={ actions[0] }
          action2={ actions[1] }
          fullyClickableParent={ fullyClickableParent }
          isKnockoutLink={ isKnockoutLink }
          style={ style }
          type={ types }
        />
      }
      { isActionGroupMulti && !fullyClickableParent &&
        <ActionGroupMulti
          actions={ actions }
          alignment={ alignment }
          isKnockoutLink={ isKnockoutLink }
          style={ style }
          types={ types }
        />
      }
      { fullyClickableParent &&
        <ActionGroupDefault
          action1={ actions[0] }
          fullyClickableParent={ fullyClickableParent }
          style={ style }
          type={ types }
        />
      }
    </div>
  );
};

/**
 * Property type definitions
 * @typedef ActionGroups
 * @type {object}
 * @property {array} actions - allows passing an array of actions
 * @property {string} alignment - sets the actions to centered
 * @property {string} buttonStyle - sets the button style for ActionGroups
 * @property {boolean} isKnockoutLink - sets the link text to white
 * @property {string} linkStyle - sets the link style for ActionGroups
 * @property {string} style - sets the style of the ActionGroups
 * @property {string} types - sets the types of the ActionGroups
 * @property {string} variant - sets the variant of the ActionGroups
 */

const propTypes = {
  /**
   * Allows passing an array of actions
   */
  actions: PropTypes.array,
  /**
   * Sets the actions to centered
   */
  alignment: PropTypes.string,
  /**
   * Sets the link text to white
   */
  isKnockoutLink: PropTypes.bool,
  /**
   * sets the style of the ActionGroups
   */
  style: PropTypes.string,
  /**
   * type component above the eyebrow
   */
  types: PropTypes.string,
  /**
   * sets the variant of the ActionGroups
   */
  variant: PropTypes.string
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} alignment - alignment: 'left' - sets the default for the alignment
 */
export const defaultProps =  {
  alignment: 'left'
};

ActionGroups.propTypes = propTypes;
ActionGroups.defaultProps = defaultProps;

export default ActionGroups;

/**
 * Processes an array of actions to update URLs for auth0 links.
 * Returns a new array with modified actions if changes are made,
 * otherwise, it returns the original array.
 *
 * @param {Array<object> | undefined | null} originalActions - The input array of action objects.
 * @param {function} getAuthUrl - Callback function to get the auth0 URL.
 * @param {function} isServer - Callback function to check if the environment is server-side.
 * @param {function} isAuthButton - Callback function to check if an action is an auth button.
 * @returns {Array<object>} The processed actions array. Returns an empty array if input is invalid or empty.
 */
export const processAuthActionUrls = ( data, methods ) => {
  const { originalActions } = handleEmptyObjects( data );
  const { getAuthUrl, isServer, isAuthButton } = methods;
  if( !Array.isArray( originalActions ) || originalActions.length === 0 ){
    return [];
  }

  let hasBeenModified = false;
  const processedActions = [...originalActions];

  // Process the first action (actions[0])
  if( processedActions[0] && isAuthButton( processedActions[0] ) ){
    let auth0Screen;
    if( processedActions[0].clientActionType === CLIENT_ACTION_TYPES.AUTH_SIGNUP ){
      auth0Screen = { screen: AUTH0_SCREEN.Signup };
    }
    const authUrl = !isServer() && getAuthUrl( auth0Screen );
    processedActions[0] = { ...processedActions[0], url: authUrl };
    hasBeenModified = true;
  }

  // Process the second action (actions[1]), if it exists
  if( processedActions.length > 1 && processedActions[1] && isAuthButton( processedActions[1] ) ){
    let auth0Screen;
    if( processedActions[1].clientActionType === CLIENT_ACTION_TYPES.AUTH_SIGNUP ){
      auth0Screen = { screen: AUTH0_SCREEN.Signup };
    }
    const authUrl = !isServer() && getAuthUrl( auth0Screen );

    processedActions[1] = { ...processedActions[1], url: authUrl };
    hasBeenModified = true;
  }

  // Return the new array if modified, otherwise the original input array (which is already a copy if it was modified once)
  // Or, to strictly adhere to returning original if no change:
  return hasBeenModified ? processedActions : originalActions;
};